import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import {
  StackView,
  Button,
  Divider,
  Spinner,
  Typography,
  Notification
} from '@telus-uds/components-web';

import queryString from 'query-string';

import request, { apiHost } from '../utils/request';
import FieldCheckbox from '../form/fields/fieldCheckbox';
import ValidateLoader from '../UI/validateLoader';
import Loading from './loading';
import useSignContractSubmit from './useSignContractSubmit';
import useOrderPdf from '../hooks/useOrderPdf';
import { ORDER_PDF_STATUS } from '../utils/orderPdf';

import { ContentWrapper, SignatureContainer } from '../design/css';
import EN from '../../EN.json';

/**
 * Shared sign-contract view. Every order-type variant renders this with its
 * own header text, contract-preview URL, and route prefixes; all signing
 * logic (duplicate-submit guard, error mapping, preview/final separation)
 * lives in useSignContractSubmit so it exists in exactly one place.
 */
const SignContractView = ({
  search,
  headerText,
  getContractUrl,
  expiredPath,
  successPath,
  persistTokenOnSuccess
}) => {
  const {
    contractSignature: {
      consetText,
      errorMessage,
      errorCheck,
      acceptButton,
      loadingMessage,
      submittingMessage,
      signatureText,
      clearText
    }
  } = EN;

  const history = useHistory();
  const [orderId, setOrderId] = useState();
  const [signature, setSignature] = useState(false);
  const [error, setError] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const signCanvas = useRef();
  const { token } = queryString.parse(search);
  const pdf = useOrderPdf();

  const {
    submitting,
    showPreview,
    setShowPreview,
    submitError,
    clearSubmitError,
    submit
  } = useSignContractSubmit({
    token,
    expiredPath,
    successPath,
    persistTokenOnSuccess
  });

  useEffect(() => {
    const checkToken = async () => {
      const { data } = await request.get(
        `/orders/validate-token/?token=${token}`
      );

      if (data.success) {
        setOrderId(data.id);
      } else {
        history.push(expiredPath);
      }
    };
    checkToken();
  }, []);

  // Worker-only contract preview: fetch the presigned URL via the shared async client using the
  // existing signing token (?redirect=false&token=...) and embed THAT url — never the 202-capable
  // backend route. The signing page never calls /pdf-jobs/:jobId/status.
  const fetchPreview = () => {
    pdf.openPdf({
      orderId,
      route: getContractUrl(orderId),
      token,
      openPdf: false
    });
  };

  useEffect(() => {
    if (orderId) fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const renderContractPdf = height => {
    if (pdf.url) {
      return (
        <embed
          src={pdf.url}
          type="application/pdf"
          width="100%"
          height={height}
        />
      );
    }
    if (pdf.status === ORDER_PDF_STATUS.FAILED) {
      return (
        <Notification variant={{ validation: 'error' }} copy="en">
          PDF could not be generated. Please try again.
        </Notification>
      );
    }
    if (pdf.status === ORDER_PDF_STATUS.NOT_READY) {
      return (
        <Notification copy="en">
          PDF is not ready yet.
        </Notification>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spinner show label="Preparing PDF..." variant={{ size: 'small' }} />
      </div>
    );
  };

  const captureSignature = (isConfirmed = false) => {
    if (submitting) return;
    if (!isConfirmed && signCanvas.current.isEmpty()) {
      setError(errorMessage);
      return;
    }
    if (!isConfirmed && !isChecked) {
      setError(errorCheck);
      return;
    }

    submit({
      signature,
      isStatusChanged: isConfirmed,
      afterPreview: () => {
        setIsChecked(false);
        // Accept (not final): the backend just persisted THIS pending signature — re-fetch so Step 2
        // embeds it (never the previous "Sign Again" signature or the unsigned document).
        if (!isConfirmed) fetchPreview();
      }
    });
  };

  const clearSignature = () => {
    if (!signCanvas.current.isEmpty()) {
      signCanvas.current.clear();
      setSignature(null);
    }
  };

  const onChangeOfSignaturePad = () => {
    setError(false);
    setSignature(signCanvas.current.getTrimmedCanvas().toDataURL());
  };

  const onChange = () => setIsChecked(!isChecked);

  const checkBoxFieldData = {
    checked: isChecked,
    name: 'consent',
    value: 'consent',
    label: consetText,
    onChange
  };

  const signAgain = () => {
    clearSubmitError();
    setShowPreview(false);
  };

  return !orderId ? (
    <Loading isVisible>
      <ValidateLoader message={loadingMessage} />
    </Loading>
  ) : (
    <>
      {showPreview ? (
        <div className="container 85%" style={{ marginTop: '-2rem' }}>
          <ContentWrapper>
            <StackView space={3}>
              <Typography heading="h2" variant={{ size: 'h2' }} block>
                Confirm
              </Typography>
              <Notification copy="en">
                Please confirm if the signature looks correct. if not then you
                can sign it again. if it looks good then just press Submit.
              </Notification>
              {renderContractPdf('800px')}
              {submitError && (
                <Notification variant={{ validation: 'error' }} copy="en">
                  {submitError}
                </Notification>
              )}
              <div>
                <Button
                  variant={{ priority: 'low' }}
                  onClick={signAgain}
                  disabled={submitting}
                >
                  Sign again
                </Button>
                <div style={{ display: 'inline', marginLeft: '1rem' }}>
                  <Button
                    onClick={() => captureSignature(true)}
                    disabled={submitting}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </StackView>
          </ContentWrapper>
        </div>
      ) : (
        <div className="container 85%" style={{ marginTop: '-2rem' }}>
          <Spinner
            fullScreen={submitting}
            label={submittingMessage}
            show={submitting}
          />
          <ContentWrapper>
            <Typography heading="h2" variant={{ size: 'h2' }} block>{headerText}</Typography>
            <Divider space={1} variant={{ inverse: true }}/>
            {renderContractPdf('600px')}
            <Divider space={1} variant={{ inverse: true }}/>
            <StackView space={3}>
              <StackView space={1}>
                <FieldCheckbox data={checkBoxFieldData} />
              </StackView>
              <Typography heading="h4" variant={{ size: 'h4' }} block>{signatureText}</Typography>
              <SignatureContainer>
                <SignatureCanvas
                  ref={signCanvas}
                  onEnd={onChangeOfSignaturePad}
                  penColor="green"
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'sigCanvas'
                  }}
                />
                {error && (
                  <Notification variant={{ validation: 'error' }} copy="en">
                    {error}
                  </Notification>
                )}
              </SignatureContainer>
              {submitError && (
                <Notification variant={{ validation: 'error' }} copy="en">
                  {submitError}
                </Notification>
              )}
              {signature && (
                <div>
                  <Button onPress={clearSignature} variant={{ text: true }}>
                    {clearText}
                  </Button>
                </div>
              )}
              <div>
                <Button
                  onClick={() => captureSignature(false)}
                  disabled={submitting}
                >
                  {acceptButton}
                </Button>
              </div>
            </StackView>
          </ContentWrapper>
        </div>
      )}
    </>
  );
};

SignContractView.propTypes = {
  search: PropTypes.string.isRequired,
  headerText: PropTypes.string.isRequired,
  getContractUrl: PropTypes.func.isRequired,
  expiredPath: PropTypes.string.isRequired,
  successPath: PropTypes.string.isRequired,
  persistTokenOnSuccess: PropTypes.bool
};

SignContractView.defaultProps = {
  persistTokenOnSuccess: false
};

const {
  contractSignature: { header, headerOcdr }
} = EN;

const SignContract = ({ location: { search } }) => (
  <SignContractView
    search={search}
    headerText={header}
    getContractUrl={id => `${apiHost}/orders/${id}/fully-managed-quote-pdf`}
    expiredPath="/sign-contract/expired"
    successPath="/sign-contract/success"
    persistTokenOnSuccess
  />
);

const SignContractSOW = ({ location: { search } }) => (
  <SignContractView
    search={search}
    headerText={header}
    getContractUrl={id => `${apiHost}/orders/${id}/fully-managed-quote-pdf`}
    expiredPath="/sign-contract-sow/expired"
    successPath="/sign-contract-sow/success"
  />
);

const SignContractOCDR = ({ location: { search } }) => (
  <SignContractView
    search={search}
    headerText={headerOcdr}
    getContractUrl={id => `${apiHost}/orders/${id}/quote-pdf`}
    expiredPath="/sign-contract-ocdr/expired"
    successPath="/sign-contract-ocdr/success"
  />
);

SignContract.propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired
};

SignContractSOW.propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired
};

SignContractOCDR.propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired
};

// SignContractCS intentionally NOT exported: CS's sign-contract flow was retired
// (see PR #618 / SOF-3472 cherry-pick) — CS orders are read-only from the webform,
// there is no /sign-contract-cs route left in routes.js. Do not reintroduce it here.
export { SignContract, SignContractSOW, SignContractOCDR, SignContractView };
